import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type { MouseEvent } from 'react';

interface ConfirmationModalProps {
  isActive: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /**
   * The click event is forwarded so an action that can fail may call
   * `event.preventDefault()` and keep the dialog up — see the note on the
   * component.
   */
  onConfirm: (event: MouseEvent<HTMLButtonElement>) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isDanger?: boolean;
}

/**
 * Reusable confirmation dialog. Used everywhere we need a yes/no
 * gate on a destructive or significant action.
 *
 * Built on shadcn AlertDialog (Radix). The caller owns the open
 * state via `isActive`. Disabled-while-pending prevents double-tap
 * on slow mutations.
 *
 * The confirm button is Radix's `Action`, which is a `Close`: it runs the
 * handler below and then requests the dialog shut, which lands here as
 * `onOpenChange(false)` → `onCancel()`. That request is skipped when the
 * handler calls `event.preventDefault()`, since Radix composes it behind a
 * `defaultPrevented` check. So a caller whose action can be refused — and which
 * must report the refusal in place rather than close as though it had worked —
 * prevents the default and closes through `isActive` itself; that is also the
 * only way the pending state below is on screen long enough to be seen. A
 * caller that ignores the event keeps closing on confirm.
 */
export default function ConfirmationModal({
  isActive,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  isDanger = false,
}: ConfirmationModalProps) {
  return (
    <AlertDialog
      open={isActive}
      onOpenChange={(open) => {
        if (!open && !isLoading) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              {cancelText}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              onClick={onConfirm}
              disabled={isLoading}
              className={cn(
                isDanger &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
              )}
            >
              {isLoading ? 'Working…' : confirmText}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
